import { useState, useEffect } from 'react';
import { Settings, RefreshCw, Save, Clock, Heart, Star, Bookmark, X, TrendingUp, Users } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card } from '../ui/card';
import { toast } from 'sonner';
import apiClient from '../../lib/api-client';
import { Switch } from '../ui/switch';

interface DiscoverySettingsData {
  skipDuration: number;
  interestDuration: number;
  superInterestDuration: number;
  shortlistDuration: number;
  priorityWeights: {
    sameCity: number;
    nearbyCities: number;
    sameState: number;
    age: number;
    hobbies: number;
    maritalStatus: number;
    education: number;
    religion: number;
  };
}

export function DiscoverySettings() {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [genderBasedDiscovery, setGenderBasedDiscovery] = useState(true);
  const [settings, setSettings] = useState<DiscoverySettingsData>({
    skipDuration: 24,
    interestDuration: 720,
    superInterestDuration: 1440,
    shortlistDuration: 2160,
    priorityWeights: {
      sameCity: 10,
      nearbyCities: 8,
      sameState: 6,
      age: 7,
      hobbies: 5,
      maritalStatus: 4,
      education: 3,
      religion: 3
    }
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await apiClient.getDiscoverySettings();

      if (response.success && response.data) {
        setSettings({
          ...settings,
          ...response.data,
          priorityWeights: {
            ...settings.priorityWeights,
            ...(response.data.priorityWeights || {})
          }
        });
        toast.success('Settings loaded');
      }

      // Fetch gender-based discovery setting
      const settingsResponse = await apiClient.get('/settings');
      if (settingsResponse.success && settingsResponse.settings?.features) {
        setGenderBasedDiscovery(settingsResponse.settings.features.genderBasedDiscovery ?? true);
      }
    } catch (error: any) {
      console.error('Error fetching settings:', error);
      // Keep default settings on error
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await apiClient.updateDiscoverySettings(settings);

      if (response.success) {
        toast.success('Settings saved successfully');
      }
    } catch (error: any) {
      console.error('Error saving settings:', error);
      toast.error(error.response?.data?.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleGenderBasedDiscoveryToggle = async (enabled: boolean) => {
    try {
      const response = await apiClient.put('/settings/features', {
        genderBasedDiscovery: enabled
      });

      if (response.success) {
        setGenderBasedDiscovery(enabled);
        toast.success(enabled ? 'Gender-based discovery enabled' : 'Gender-based discovery disabled');
      }
    } catch (error: any) {
      console.error('Error updating gender-based discovery:', error);
      toast.error(error.response?.data?.message || 'Failed to update setting');
    }
  };

  const hoursToDays = (hours: number) => Math.round(hours / 24);
  const daysToHours = (days: number) => days * 24;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Settings className="size-6 text-pink-600" />
          <div>
            <h2 className="text-2xl">Discovery Settings</h2>
            <p className="text-sm text-gray-600">
              Configure smart matching algorithm and interaction durations
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={fetchSettings}
            disabled={loading}
          >
            <RefreshCw className={`size-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-gradient-to-r from-pink-600 to-red-600 text-white"
          >
            <Save className="size-4 mr-2" />
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="size-8 animate-spin text-pink-600" />
        </div>
      ) : (
        <>
          {/* Interaction Durations */}
          <Card className="p-6">
            <h3 className="text-lg mb-4 flex items-center gap-2">
              <Clock className="size-5 text-pink-600" />
              Interaction Durations
            </h3>
            <p className="text-sm text-gray-600 mb-6">
              Set how long profiles stay hidden after different interactions
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Skip Duration */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <X className="size-4 text-gray-500" />
                  Skip Duration (Days)
                </Label>
                <Input
                  type="number"
                  min="1"
                  value={hoursToDays(settings.skipDuration)}
                  onChange={(e) => setSettings({
                    ...settings,
                    skipDuration: daysToHours(parseInt(e.target.value) || 1)
                  })}
                />
                <p className="text-xs text-gray-500">
                  How long a skipped profile stays hidden ({hoursToDays(settings.skipDuration)} days = {settings.skipDuration} hours)
                </p>
              </div>

              {/* Interest Duration */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Heart className="size-4 text-pink-500" />
                  Interest Duration (Days)
                </Label>
                <Input
                  type="number"
                  min="1"
                  value={hoursToDays(settings.interestDuration)}
                  onChange={(e) => setSettings({
                    ...settings,
                    interestDuration: daysToHours(parseInt(e.target.value) || 1)
                  })}
                />
                <p className="text-xs text-gray-500">
                  How long interest remains valid ({hoursToDays(settings.interestDuration)} days = {settings.interestDuration} hours)
                </p>
              </div>

              {/* Super Interest Duration */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Star className="size-4 text-amber-500" />
                  Super Interest Duration (Days)
                </Label>
                <Input
                  type="number"
                  min="1"
                  value={hoursToDays(settings.superInterestDuration)}
                  onChange={(e) => setSettings({
                    ...settings,
                    superInterestDuration: daysToHours(parseInt(e.target.value) || 1)
                  })}
                />
                <p className="text-xs text-gray-500">
                  How long super interest remains valid ({hoursToDays(settings.superInterestDuration)} days = {settings.superInterestDuration} hours)
                </p>
              </div>

              {/* Shortlist Duration */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Bookmark className="size-4 text-blue-500" />
                  Shortlist Duration (Days)
                </Label>
                <Input
                  type="number"
                  min="1"
                  value={hoursToDays(settings.shortlistDuration)}
                  onChange={(e) => setSettings({
                    ...settings,
                    shortlistDuration: daysToHours(parseInt(e.target.value) || 1)
                  })}
                />
                <p className="text-xs text-gray-500">
                  How long shortlisted profiles remain hidden ({hoursToDays(settings.shortlistDuration)} days = {settings.shortlistDuration} hours)
                </p>
              </div>
            </div>
          </Card>

          {/* Priority Weights */}
          <Card className="p-6">
            <h3 className="text-lg mb-4 flex items-center gap-2">
              <TrendingUp className="size-5 text-pink-600" />
              Smart Matching Algorithm Weights
            </h3>
            <p className="text-sm text-gray-600 mb-6">
              Configure priority weights for the discovery algorithm (1-10, higher = more important)
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Same City */}
              <div className="space-y-2">
                <Label>Same City Priority</Label>
                <div className="flex items-center gap-3">
                  <Input
                    type="range"
                    min="1"
                    max="10"
                    value={settings.priorityWeights?.sameCity || 10}
                    onChange={(e) => setSettings({
                      ...settings,
                      priorityWeights: {
                        ...settings.priorityWeights,
                        sameCity: parseInt(e.target.value)
                      }
                    })}
                    className="flex-1"
                  />
                  <span className="text-lg font-semibold w-8 text-center">
                    {settings.priorityWeights?.sameCity || 10}
                  </span>
                </div>
                <p className="text-xs text-gray-500">
                  Profiles from the same city
                </p>
              </div>

              {/* Nearby Cities */}
              <div className="space-y-2">
                <Label>Nearby Cities Priority</Label>
                <div className="flex items-center gap-3">
                  <Input
                    type="range"
                    min="1"
                    max="10"
                    value={settings.priorityWeights?.nearbyCities || 8}
                    onChange={(e) => setSettings({
                      ...settings,
                      priorityWeights: {
                        ...settings.priorityWeights,
                        nearbyCities: parseInt(e.target.value)
                      }
                    })}
                    className="flex-1"
                  />
                  <span className="text-lg font-semibold w-8 text-center">
                    {settings.priorityWeights?.nearbyCities || 8}
                  </span>
                </div>
                <p className="text-xs text-gray-500">
                  Profiles from nearby cities
                </p>
              </div>

              {/* Same State */}
              <div className="space-y-2">
                <Label>Same State Priority</Label>
                <div className="flex items-center gap-3">
                  <Input
                    type="range"
                    min="1"
                    max="10"
                    value={settings.priorityWeights?.sameState || 6}
                    onChange={(e) => setSettings({
                      ...settings,
                      priorityWeights: {
                        ...settings.priorityWeights,
                        sameState: parseInt(e.target.value)
                      }
                    })}
                    className="flex-1"
                  />
                  <span className="text-lg font-semibold w-8 text-center">
                    {settings.priorityWeights?.sameState || 6}
                  </span>
                </div>
                <p className="text-xs text-gray-500">
                  Profiles from the same state
                </p>
              </div>

              {/* Age */}
              <div className="space-y-2">
                <Label>Age Compatibility Priority</Label>
                <div className="flex items-center gap-3">
                  <Input
                    type="range"
                    min="1"
                    max="10"
                    value={settings.priorityWeights?.age || 7}
                    onChange={(e) => setSettings({
                      ...settings,
                      priorityWeights: {
                        ...settings.priorityWeights,
                        age: parseInt(e.target.value)
                      }
                    })}
                    className="flex-1"
                  />
                  <span className="text-lg font-semibold w-8 text-center">
                    {settings.priorityWeights?.age || 7}
                  </span>
                </div>
                <p className="text-xs text-gray-500">
                  Age difference (within 5 years)
                </p>
              </div>

              {/* Hobbies */}
              <div className="space-y-2">
                <Label>Hobbies Match Priority</Label>
                <div className="flex items-center gap-3">
                  <Input
                    type="range"
                    min="1"
                    max="10"
                    value={settings.priorityWeights?.hobbies || 5}
                    onChange={(e) => setSettings({
                      ...settings,
                      priorityWeights: {
                        ...settings.priorityWeights,
                        hobbies: parseInt(e.target.value)
                      }
                    })}
                    className="flex-1"
                  />
                  <span className="text-lg font-semibold w-8 text-center">
                    {settings.priorityWeights?.hobbies || 5}
                  </span>
                </div>
                <p className="text-xs text-gray-500">
                  Common hobbies and interests
                </p>
              </div>

              {/* Marital Status */}
              <div className="space-y-2">
                <Label>Marital Status Priority</Label>
                <div className="flex items-center gap-3">
                  <Input
                    type="range"
                    min="1"
                    max="10"
                    value={settings.priorityWeights?.maritalStatus || 4}
                    onChange={(e) => setSettings({
                      ...settings,
                      priorityWeights: {
                        ...settings.priorityWeights,
                        maritalStatus: parseInt(e.target.value)
                      }
                    })}
                    className="flex-1"
                  />
                  <span className="text-lg font-semibold w-8 text-center">
                    {settings.priorityWeights?.maritalStatus || 4}
                  </span>
                </div>
                <p className="text-xs text-gray-500">
                  Same marital status match
                </p>
              </div>

              {/* Education */}
              <div className="space-y-2">
                <Label>Education Level Priority</Label>
                <div className="flex items-center gap-3">
                  <Input
                    type="range"
                    min="1"
                    max="10"
                    value={settings.priorityWeights?.education || 3}
                    onChange={(e) => setSettings({
                      ...settings,
                      priorityWeights: {
                        ...settings.priorityWeights,
                        education: parseInt(e.target.value)
                      }
                    })}
                    className="flex-1"
                  />
                  <span className="text-lg font-semibold w-8 text-center">
                    {settings.priorityWeights?.education || 3}
                  </span>
                </div>
                <p className="text-xs text-gray-500">
                  Same education level match
                </p>
              </div>

              {/* Religion */}
              <div className="space-y-2">
                <Label>Religion Match Priority</Label>
                <div className="flex items-center gap-3">
                  <Input
                    type="range"
                    min="1"
                    max="10"
                    value={settings.priorityWeights?.religion || 3}
                    onChange={(e) => setSettings({
                      ...settings,
                      priorityWeights: {
                        ...settings.priorityWeights,
                        religion: parseInt(e.target.value)
                      }
                    })}
                    className="flex-1"
                  />
                  <span className="text-lg font-semibold w-8 text-center">
                    {settings.priorityWeights?.religion || 3}
                  </span>
                </div>
                <p className="text-xs text-gray-500">
                  Same religion match
                </p>
              </div>
            </div>
          </Card>

          {/* Gender-Based Discovery */}
          <Card className="p-6">
            <h3 className="text-lg mb-4 flex items-center gap-2">
              <Users className="size-5 text-pink-600" />
              Gender-Based Discovery
            </h3>
            <p className="text-sm text-gray-600 mb-6">
              Enable or disable gender-based discovery to show profiles of the opposite gender
            </p>

            <div className="flex items-center gap-3">
              <Switch
                checked={genderBasedDiscovery}
                onCheckedChange={handleGenderBasedDiscoveryToggle}
              />
              <Label className="text-sm">
                {genderBasedDiscovery ? 'Enabled' : 'Disabled'}
              </Label>
            </div>
          </Card>

          {/* Info Card */}
          <Card className="p-6 bg-blue-50 border-blue-200">
            <div className="flex gap-3">
              <div className="text-blue-600">
                <Settings className="size-5" />
              </div>
              <div className="space-y-2">
                <h4 className="font-semibold text-blue-900">How Discovery Algorithm Works</h4>
                <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
                  <li>Profiles are ranked based on compatibility scores calculated from priority weights</li>
                  <li>Higher priority weights give more importance to specific matching criteria</li>
                  <li>Profiles with interactions (skip, interest, shortlist) are hidden for the configured duration</li>
                  <li>After expiration, profiles may reappear in discovery based on algorithm</li>
                  <li>Same city gets highest default priority (10), followed by age (7) and nearby cities (8)</li>
                </ul>
              </div>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}